import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PhotoIcon } from '@heroicons/react/24/solid';
import axios from 'axios';
import Navbar from '../navbar/Navbar';

const UpdateJournal = () => {
  const [title, setTitle] = useState('');
  const [article, setArticle] = useState('');
  const [tags, setTags] = useState('');
  const [coverPicture, setCoverPicture] = useState(null);
  const [error, setError] = useState('');
  const { username, id } = useParams();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchJournal = async () => {
      try {
        const response = await axios.get(`https://happyu.onrender.com/${username}/${id}`);
        if (response.status === 200) {
          const { title, article, tags, coverPicture } = response.data;
          setTitle(title);
          setArticle(article);
          setTags(tags.join(', '));
          setCoverPicture(coverPicture);
        } else {
          setError('Failed to fetch journal details');
        }
      } catch (error) {
        setError('Failed to fetch journal details');
      }
    };
    fetchJournal();
  }, [username, id]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name === 'title') setTitle(value);
    if (name === 'article') setArticle(value);
    if (name === 'tags') setTags(value);
  };

  const handleFileChange = (e) => setCoverPicture(e.target.files[0]);

  const handleCancel = () => navigate(`/${username}/profile`);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const formData = new FormData();
      formData.append('title', title);
      formData.append('article', article);
      formData.append('tags', tags);
      if (coverPicture) formData.append('coverPicture', coverPicture);

      const formDataObject = {};
      for (const [key, value] of formData.entries()) formDataObject[key] = value;

      const response = await axios.put(`https://happyu.onrender.com/journals/${username}/${id}`, formDataObject);

      if (response.status !== 200) {
        throw new Error('Network response was not ok');
      } else {
        navigate(`/${username}/profile`);
      }
    } catch (error) {
      setError('Failed to update journal');
    }
  };

  return (
    <>
      <Navbar />
      <div className="min-h-screen flex items-center justify-center py-10 px-2 bg-gradient-to-r from-indigo-50 via-teal-50 to-white">
        <div className="max-w-lg w-full bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl border border-teal-100 px-8 py-10">
          <h2 className="text-3xl font-bold text-indigo-700 mb-6">Update your journal</h2>
          <form onSubmit={handleSubmit} className="space-y-7">
            <div>
              <label htmlFor="coverPicture" className="block text-sm font-semibold text-gray-700 mb-2">
                Cover Photo
              </label>
              <div className="mt-1 flex flex-col items-start px-6 py-6 border-2 border-dashed border-teal-300 bg-gradient-to-r from-teal-50 to-indigo-50 rounded-lg hover:border-indigo-400 transition">
                <div className="flex items-center gap-4">
                  <PhotoIcon className="h-10 w-10 text-teal-300" aria-hidden="true" />
                  <label htmlFor="file-upload" className="cursor-pointer rounded-md font-medium text-teal-600 hover:text-indigo-600 focus:ring-2 focus:ring-offset-2 focus:ring-teal-400 px-3 py-2 bg-white shadow">
                    <span>Upload file</span>
                    <input id="file-upload" name="coverPicture" type="file" onChange={handleFileChange} className="sr-only" />
                  </label>
                  <span className="ml-2 text-xs text-gray-400">PNG, JPG, GIF up to 10MB</span>
                </div>
                {coverPicture && (
                  <span className="mt-2 text-sm text-teal-700 font-medium">
                    Selected: {coverPicture.name || coverPicture}
                  </span>
                )}
              </div>
            </div>
            <div>
              <label htmlFor="title" className="block text-sm font-semibold text-gray-700 mb-1">Title</label>
              <input
                type="text"
                name="title"
                id="title"
                value={title}
                onChange={handleChange}
                className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
                placeholder="Your journal title"
                required
              />
            </div>
            <div>
              <label htmlFor="article" className="block text-sm font-semibold text-gray-700 mb-1">Your Journal</label>
              <textarea
                id="article"
                name="article"
                rows={7}
                value={article}
                onChange={handleChange}
                className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
                placeholder="Write your journal here..."
                required
              />
              <p className="mt-2 text-sm text-gray-400">Express yourself!</p>
            </div>
            <div>
              <label htmlFor="tags" className="block text-sm font-semibold text-gray-700 mb-1">Tags</label>
              <input
                type="text"
                name="tags"
                id="tags"
                value={tags}
                onChange={handleChange}
                className="mt-2 block w-full rounded-md border-0 py-2 px-3 text-gray-900 shadow ring-1 ring-inset ring-teal-200 focus:ring-2 focus:ring-indigo-500"
                placeholder="e.g., mindfulness, gratitude"
              />
            </div>
            {error && <p className="text-red-500 text-left mt-2">{error}</p>}
            <div className="mt-8 flex justify-end gap-4">
              <button
                type="button"
                onClick={handleCancel}
                className="text-base font-semibold text-gray-700 px-5 py-2 bg-gray-100 rounded-md hover:bg-gray-200 transition"
              >
                Cancel
              </button>
              <button
                className="bg-indigo-600 hover:bg-teal-500 text-white font-bold px-6 py-2 rounded-md shadow transition focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-600"
                type="submit"
              >
                Save
              </button>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default UpdateJournal;
